/**
 * Shared subscription message totals — must stay in sync with Messages page stats cards.
 * GET /usage/balance and GET /agents aggregate these fields from subscription / customer usage.
 *
 * Balance may be `{ subscription: {...} }` or `{ subscriptions: [{ ... }] }` (per backend).
 */

import { getFacebookRemainingMessageCount, getFacebookTotalMessageCount } from './subscriptionQuota';

const toNumber = (value, fallback = 0) => {
  const num = Number(value);
  return Number.isNaN(num) ? fallback : num;
};

/** Axios body: `{ balance }`, flat payload, or already-unwrapped `data`. */
export function getBalancePayload(raw) {
  if (raw == null) return {};
  const d = raw?.data?.balance ?? raw?.data ?? raw?.balance ?? raw;
  return d && typeof d === 'object' ? d : {};
}

/** Sum a numeric field across `subscriptions[]`; returns null if no subscriptions or no values. */
function aggregateFromSubscriptions(data, field) {
  const subs = data?.subscriptions;
  if (!Array.isArray(subs) || subs.length === 0) return null;
  let sum = 0;
  let any = false;
  for (const sub of subs) {
    if (!sub || sub[field] === undefined || sub[field] === null || sub[field] === '') continue;
    sum += toNumber(sub[field], 0);
    any = true;
  }
  return any ? sum : null;
}

export const getFirstNumeric = (sources, keys, fallback = 0) => {
  for (const source of sources) {
    if (!source) continue;
    for (const key of keys) {
      const value = source[key];
      if (value !== undefined && value !== null && value !== '') {
        return toNumber(value, fallback);
      }
    }
  }
  return fallback;
};

const REMAINING_KEYS = [
  'remaining_message_count',
  'subscription_remaining_message_count',
  'subscription_messages_remaining',
];
const USED_KEYS = [
  'used_message_count',
  'subscription_used_message_count',
  'subscription_messages_used',
];
const TOTAL_KEYS = [
  'total_message_count',
  'subscription_total_message_count',
  'subscription_messages_total',
];

/**
 * @param {object|null|undefined} raw — full axios response or `data` from GET /usage/balance
 */
export function normalizeSubscriptionMessagesBalance(raw) {
  const data = getBalancePayload(raw);
  const legacy = data?.subscription || null;

  const subscriptionRemainingMessages =
    aggregateFromSubscriptions(data, 'remaining_message_count') ??
    getFirstNumeric([legacy, data], REMAINING_KEYS, 0);

  const subscriptionUsedMessages =
    aggregateFromSubscriptions(data, 'used_message_count') ??
    getFirstNumeric([legacy, data], USED_KEYS, 0);

  const subscriptionTotalMessages =
    aggregateFromSubscriptions(data, 'total_message_count') ??
    getFirstNumeric(
      [legacy, data],
      TOTAL_KEYS,
      subscriptionRemainingMessages + subscriptionUsedMessages
    );

  const subscription =
    legacy || (Array.isArray(data.subscriptions) && data.subscriptions[0]) || null;

  return {
    subscriptionRemainingMessages,
    subscriptionUsedMessages,
    subscriptionTotalMessages,
    subscription,
  };
}

/**
 * @param {Array} list — agents from GET /agents
 */
export function normalizeSubscriptionMessagesFromAgents(list) {
  const source = Array.isArray(list) ? list : [];
  const subscriptionRemainingMessages = source.reduce(
    (sum, agent) => sum + getFirstNumeric([agent?.subscription, agent], REMAINING_KEYS, 0),
    0
  );
  const subscriptionUsedMessages = source.reduce(
    (sum, agent) => sum + getFirstNumeric([agent?.subscription, agent], USED_KEYS, 0),
    0
  );
  const subscriptionTotalMessages = source.reduce(
    (sum, agent) => sum + getFirstNumeric([agent?.subscription, agent], TOTAL_KEYS, 0),
    0
  );
  return {
    subscriptionRemainingMessages,
    subscriptionUsedMessages,
    subscriptionTotalMessages:
      subscriptionTotalMessages || subscriptionRemainingMessages + subscriptionUsedMessages,
  };
}

/**
 * Voice minutes used (plan/subscription), from balance — preferred over summing GET /calls rows.
 * Reads `subscriptions[].minutes_used` (current API), then legacy single-subscription keys.
 * Returns null if nothing matches (caller may fall back to summing calls).
 */
export function getUsedVoiceMinutesFromBalance(rawBalanceResponse) {
  const data = getBalancePayload(rawBalanceResponse);

  const fromSubs = aggregateFromSubscriptions(data, 'minutes_used');
  if (fromSubs != null) return fromSubs;

  const nested = [data, data.subscription, data.usage, data.voice].filter(Boolean);
  const keys = [
    'minutes_used',
    'call_minutes_used',
    'voice_minutes_used',
    'calls_minutes_used',
    'total_call_minutes',
    'total_voice_minutes',
    'total_minutes_used',
    'minutes_used_for_calls',
  ];
  for (const obj of nested) {
    for (const key of keys) {
      const v = obj[key];
      if (v !== undefined && v !== null && v !== '') {
        const n = Number(v);
        if (Number.isFinite(n) && n >= 0) return n;
      }
    }
  }
  return null;
}

/**
 * Voice minutes remaining — `subscriptions[].minutes_remaining`, then root pool fields.
 */
export function getRemainingVoiceMinutesFromBalance(rawBalanceResponse) {
  const data = getBalancePayload(rawBalanceResponse);

  const fromSubs = aggregateFromSubscriptions(data, 'minutes_remaining');
  if (fromSubs != null) return fromSubs;

  const candidates = [
    data.minutes_remaining,
    data.remaining_minutes,
    data.subscription_minutes,
    data.total_remaining,
    data.subscription?.minutes_remaining,
    data.subscription?.remaining_minutes,
    data.package_minutes_remaining,
    data.total_minutes,
  ];
  for (const c of candidates) {
    if (c !== undefined && c !== null && c !== '') {
      const n = Number(c);
      if (Number.isFinite(n) && n >= 0) return n;
    }
  }
  return 0;
}

/**
 * Total voice minutes pool for dashboard (plan + usage), from GET /usage/balance when present.
 */
export function getTotalVoiceMinutesAvailableFromBalance(rawBalanceResponse) {
  const data = getBalancePayload(rawBalanceResponse);
  const explicit = getFirstNumeric(
    [data, data.subscription],
    ['total_minutes', 'voice_minutes_total', 'subscription_voice_minutes_total', 'included_voice_minutes'],
    null
  );
  if (explicit != null && explicit > 0) return explicit;
  const remaining = getRemainingVoiceMinutesFromBalance(rawBalanceResponse);
  const used = getUsedVoiceMinutesFromBalance(rawBalanceResponse);
  if (used != null && Number.isFinite(used)) return Math.max(0, remaining + used);
  if (remaining > 0) return remaining;
  return explicit ?? 0;
}

export function normalizeVoiceMinutesQuota(rawBalanceResponse) {
  const remaining = getRemainingVoiceMinutesFromBalance(rawBalanceResponse);
  const total = getTotalVoiceMinutesAvailableFromBalance(rawBalanceResponse);
  return {
    remaining,
    total: Math.max(total ?? 0, remaining ?? 0),
  };
}

const VOICE_REM_AGENT = [
  'minutes_remaining',
  'subscription_minutes_remaining',
  'remaining_voice_minutes',
  'voice_minutes_remaining',
  'included_minutes_remaining',
];
const VOICE_USED_AGENT = [
  'minutes_used',
  'call_minutes_used',
  'voice_minutes_used',
  'calls_minutes_used',
  'subscription_minutes_used',
];
const VOICE_TOTAL_AGENT = [
  'total_minutes',
  'minutes_per_month',
  'included_minutes',
  'subscription_total_minutes',
  'voice_minutes_total',
];

/** Voice pool for one agent row (GET /agents/:id or list item). */
export function normalizeVoiceMinutesQuotaForAgent(agent) {
  if (!agent) return { remaining: 0, total: 0 };
  const src = [agent?.subscription, agent];
  const remaining = getFirstNumeric(src, VOICE_REM_AGENT, 0);
  const used = getFirstNumeric(src, VOICE_USED_AGENT, null);
  const totalExplicit = getFirstNumeric(src, VOICE_TOTAL_AGENT, null);
  let total =
    totalExplicit != null && totalExplicit > 0 ? totalExplicit : null;
  if (total == null && used != null && Number.isFinite(used)) total = remaining + used;
  if (total == null) total = remaining;
  return { remaining, total: Math.max(total ?? 0, remaining) };
}

/** SMS / general message pool for one agent. */
export function normalizeSmsQuotaForAgent(agent) {
  if (!agent) return { remaining: 0, total: 0 };
  const src = [agent?.subscription, agent];
  const remaining = getFirstNumeric(src, REMAINING_KEYS, 0);
  const used = getFirstNumeric(src, USED_KEYS, 0);
  const total = getFirstNumeric(src, TOTAL_KEYS, remaining + used);
  return { remaining, total: Math.max(total, remaining) };
}

/** Facebook pool for one agent (subscription + root aliases). */
export function normalizeFacebookQuotaForAgent(agent) {
  if (!agent) return { remaining: 0, total: 0 };
  const r = toNumber(getFacebookRemainingMessageCount(agent), 0);
  const t = toNumber(getFacebookTotalMessageCount(agent), 0);
  return { remaining: r, total: Math.max(t, r) };
}

/**
 * Facebook message pool: account rollups on balance (subscription_facebook_*), else sum from GET /agents rows.
 */
export function normalizeFacebookQuota(rawBalance, agents = []) {
  const data = getBalancePayload(rawBalance);

  let remaining =
    data.subscription_facebook_remaining ??
    data.remaining_facebook_message_count ??
    aggregateFromSubscriptions(data, 'remaining_facebook_message_count');

  let total =
    data.subscription_facebook_total ??
    data.total_facebook_message_count ??
    aggregateFromSubscriptions(data, 'total_facebook_message_count');

  if (remaining === undefined || remaining === null) {
    const list = Array.isArray(agents) ? agents : [];
    remaining = list.reduce((sum, a) => sum + toNumber(getFacebookRemainingMessageCount(a), 0), 0);
  }
  if (total === undefined || total === null) {
    const list = Array.isArray(agents) ? agents : [];
    total = list.reduce((sum, a) => sum + toNumber(getFacebookTotalMessageCount(a), 0), 0);
  }

  const r = toNumber(remaining, 0);
  const t = toNumber(total, 0);
  return { remaining: r, total: Math.max(t, r) };
}

/** `< 10%` remaining → critical; `<= 30%` → warning; else null. Assumes total > 0. */
export function getQuotaAlertLevel(remaining, total) {
  const r = Number(remaining);
  const t = Number(total);
  if (!Number.isFinite(r) || !Number.isFinite(t) || t <= 0) return null;
  const ratio = r / t;
  if (ratio < 0.1) return 'critical';
  if (ratio <= 0.3) return 'warning';
  return null;
}
